// Enhanced Schema Generators for GCC Tax Laws

// Schema for Articles
export const generateArticleSchema = (article) => {
  const countryName = getCountryName(article.slug)
  const lawName = getLawName(article.slug)

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LegalDocument',
    '@id': `https://gcctaxlaws.com/articles/${article.slug}`,
    name: article.title,
    headline: article.title,
    description: article.summary || extractFirstSentence(article.content),
    url: `https://gcctaxlaws.com/articles/${article.slug}`,
    datePublished: formatDate(article.createdAt),
    dateModified: formatDate(article.updatedAt),
    author: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: 'https://gcctaxlaws.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://gcctaxlaws.com/images/logo.png',
        width: 180,
        height: 60,
      },
    },
    publisher: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: 'https://gcctaxlaws.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://gcctaxlaws.com/images/logo.png',
        width: 180,
        height: 60,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://gcctaxlaws.com/articles/${article.slug}`,
    },
    about: {
      '@type': 'Thing',
      name: 'Tax Law',
      description: `${countryName} tax legislation and regulations`,
    },
    mentions: [
      {
        '@type': 'Organization',
        name: getIssuingAuthority(article.slug),
      },
      {
        '@type': 'Place',
        name: countryName,
      },
    ],
    legislationType: 'Tax Law Article',
    jurisdiction: {
      '@type': 'Place',
      name: countryName,
    },
    inLanguage: {
      '@type': 'Language',
      name: 'English',
      alternateName: 'en',
    },
    isPartOf: {
      '@type': 'Legislation',
      name: lawName,
      jurisdiction: countryName,
    },
    keywords: generateKeywords(article, 'article'),
    // Add structured data for article number if available
    ...(article.number && {
      identifier: {
        '@type': 'PropertyValue',
        name: 'Article Number',
        value: article.number,
      },
    }),
    // Add word count for better content understanding
    ...(article.content && {
      wordCount: getWordCount(article.content),
    }),
  }

  // Add breadcrumb if path exists
  if (article.path && article.path.length > 0) {
    schema.breadcrumb = {
      '@type': 'BreadcrumbList',
      itemListElement: article.path.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: `https://gcctaxlaws.com/laws/${getCountryCode(
          article.slug
        )}/${getLawShortName(article.slug)}/${item.fullPath}`,
      })),
    }
  }

  // Add related articles if available
  if (article.relatedArticles && article.relatedArticles.length > 0) {
    schema.relatedLink = article.relatedArticles.map(
      (related) => `https://gcctaxlaws.com/articles/${related.slug}`
    )
  }

  return schema
}

// Schema for Decisions
export const generateDecisionSchema = (decision) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'LegalRuling',
    '@id': `https://gcctaxlaws.com/decisions/${decision.slug}`,
    name: decision.title,
    headline: decision.title,
    description: decision.summary || extractFirstSentence(decision.content),
    url: `https://gcctaxlaws.com/decisions/${decision.slug}`,
    datePublished: formatDate(decision.createdAt),
    dateModified: formatDate(decision.updatedAt),
    dateCreated: formatDate(decision.issueDate || decision.createdAt),
    author: {
      '@type': 'Organization',
      name: 'Federal Tax Authority',
      url: 'https://tax.gov.ae',
      alternateName: 'FTA',
    },
    publisher: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: 'https://gcctaxlaws.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://gcctaxlaws.com/decisions/${decision.slug}`,
    },
    about: {
      '@type': 'Thing',
      name: 'Tax Decision',
      description: 'Official tax authority ruling and decision',
    },
    legislationType: decision.type || 'Tax Decision',
    jurisdiction: {
      '@type': 'Place',
      name: 'United Arab Emirates',
    },
    inLanguage: {
      '@type': 'Language',
      name: 'English',
      alternateName: 'en',
    },
    isBasedOn: {
      '@type': 'Legislation',
      name: getLawName(decision.slug) || 'UAE Tax Law',
    },
    keywords: generateKeywords(decision, 'decision'),
    // Add decision-specific identifiers
    ...(decision.number && {
      identifier: {
        '@type': 'PropertyValue',
        name: 'Decision Number',
        value: decision.number,
      },
    }),
    ...(decision.year && {
      temporalCoverage: decision.year.toString(),
    }),
    // Add decision category
    ...(decision.category && {
      category: decision.category,
    }),
    // Add word count
    ...(decision.content && {
      wordCount: getWordCount(decision.content),
    }),
  }
}

// Schema for Guidelines
export const generateGuidelineSchema = (guideline) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'GovernmentService',
    '@id': `https://gcctaxlaws.com/guidances/${guideline.slug}`,
    name: guideline.title,
    headline: guideline.title,
    description: guideline.summary || extractFirstSentence(guideline.content),
    url: `https://gcctaxlaws.com/guidances/${guideline.slug}`,
    datePublished: formatDate(guideline.createdAt),
    dateModified: formatDate(guideline.updatedAt),
    provider: {
      '@type': 'Organization',
      name: 'Federal Tax Authority',
      url: 'https://tax.gov.ae',
      alternateName: 'FTA',
    },
    publisher: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: 'https://gcctaxlaws.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://gcctaxlaws.com/guidances/${guideline.slug}`,
    },
    serviceType: guideline.type || 'Tax Guidance',
    category: 'Tax Compliance',
    areaServed: {
      '@type': 'Country',
      name: 'United Arab Emirates',
      alternateName: 'UAE',
    },
    availableLanguage: {
      '@type': 'Language',
      name: 'English',
      alternateName: 'en',
    },
    about: {
      '@type': 'Thing',
      name: 'Tax Guidance',
      description: 'Official tax authority guidance and procedures',
    },
    keywords: generateKeywords(guideline, 'guidance'),
    // Add guidance-specific identifiers
    ...(guideline.uniqueCode && {
      identifier: {
        '@type': 'PropertyValue',
        name: 'Guidance Code',
        value: guideline.uniqueCode,
      },
    }),
    // Add related law information
    ...(guideline.lawSlug && {
      isRelatedTo: {
        '@type': 'Legislation',
        name: getLawName(guideline.lawSlug),
      },
    }),
    // Add audience information
    audience: {
      '@type': 'Audience',
      audienceType: 'Taxpayers, Tax Advisors, Businesses',
    },
    // Add word count
    ...(guideline.content && {
      wordCount: getWordCount(guideline.content),
    }),
  }
}

// Schema for Tax Treaties
export const generateTreatySchema = (treaty) => {
  const isOfficial = treaty.officialTranslation !== false

  return {
    '@context': 'https://schema.org',
    '@type': 'LegalDocument',
    '@id': `https://gcctaxlaws.com/tax-treaties/${treaty.slug}`,
    name: treaty.title,
    headline: treaty.title,
    description:
      treaty.summary ||
      `Double Taxation Avoidance Agreement between UAE and ${treaty.country2Name}`,
    url: `https://gcctaxlaws.com/tax-treaties/${treaty.slug}`,
    datePublished: formatDate(treaty.createdAt),
    dateModified: formatDate(treaty.updatedAt),
    author: [
      {
        '@type': 'Organization',
        name: 'Government of UAE',
      },
      {
        '@type': 'Organization',
        name: `Government of ${treaty.country2Name}`,
      },
    ],
    publisher: {
      '@type': 'Organization',
      name: 'GCC Tax Laws',
      url: 'https://gcctaxlaws.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://gcctaxlaws.com/tax-treaties/${treaty.slug}`,
    },
    legislationType: 'International Tax Treaty',
    jurisdiction: [
      {
        '@type': 'Place',
        name: 'United Arab Emirates',
      },
      {
        '@type': 'Place',
        name: treaty.country2Name,
      },
    ],
    inLanguage: {
      '@type': 'Language',
      name: 'English',
      alternateName: 'en',
    },
    about: {
      '@type': 'Thing',
      name: 'Double Taxation Avoidance',
      description: 'International tax treaty to prevent double taxation',
    },
    mentions: [
      {
        '@type': 'Place',
        name: 'United Arab Emirates',
      },
      {
        '@type': 'Place',
        name: treaty.country2Name,
      },
    ],
    keywords: generateKeywords(treaty, 'treaty'),
    // Add treaty-specific information
    ...(treaty.issueDate && {
      dateCreated: formatDate(treaty.issueDate),
    }),
    // Add translation status
    translationOfWork: isOfficial
      ? undefined
      : {
          '@type': 'CreativeWork',
          name: `Original ${treaty.country2Name} Tax Treaty`,
          inLanguage: 'Original Language',
        },
    // Add MLI information if available
    ...(treaty.mliContent && {
      hasPart: {
        '@type': 'LegalDocument',
        name: 'Multilateral Instrument (MLI) Provisions',
        description:
          'OECD Multilateral Convention provisions applicable to this treaty',
      },
    }),
    // Add bilateral relationship
    isPartOf: {
      '@type': 'Collection',
      name: 'UAE Double Taxation Agreements',
      description: 'Collection of bilateral tax treaties signed by UAE',
    },
    // Add word count
    ...(treaty.content && {
      wordCount: getWordCount(treaty.content),
    }),
  }
}

// Schema for Website/Organization (Enhanced)
export const generateWebsiteSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': 'https://gcctaxlaws.com/#website',
    name: 'GCC Tax Laws',
    alternateName: 'GCCTL',
    description:
      'Premier GCC Tax Research Platform for tax advisors, corporations, and academic institutions',
    url: 'https://gcctaxlaws.com',
    inLanguage: 'en',
    copyrightYear: new Date().getFullYear(),
    dateModified: new Date().toISOString(),
    potentialAction: [
      {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate:
            'https://gcctaxlaws.com/search-across-law?query={search_term_string}',
        },
        'query-input': 'required name=search_term_string',
      },
      {
        '@type': 'ReadAction',
        target: 'https://gcctaxlaws.com/articles',
      },
    ],
    publisher: {
      '@type': 'Organization',
      '@id': 'https://gcctaxlaws.com/#organization',
      name: 'GCC Tax Laws',
      url: 'https://gcctaxlaws.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://gcctaxlaws.com/images/logo.png',
        width: 180,
        height: 60,
      },
      description: 'Leading provider of GCC tax law information and research',
      foundingDate: '2020',
      knowsAbout: [
        'UAE Tax Law',
        'GCC Tax Regulations',
        'Corporate Income Tax',
        'Value Added Tax',
        'Tax Treaties',
        'Tax Compliance',
      ],
      areaServed: {
        '@type': 'Place',
        name: 'Gulf Cooperation Council',
      },
      sameAs: [
        'https://linkedin.com/company/gcctaxlaws',
        'https://twitter.com/gcctaxlaws',
      ],
    },
    mainEntity: {
      '@type': 'Organization',
      '@id': 'https://gcctaxlaws.com/#organization',
    },
  }
}

// Schema for Organization
export const generateOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://gcctaxlaws.com/#organization',
    name: 'GCC Tax Laws',
    alternateName: 'GCCTL',
    url: 'https://gcctaxlaws.com',
    logo: {
      '@type': 'ImageObject',
      url: 'https://gcctaxlaws.com/images/logo.png',
      width: 180,
      height: 60,
    },
    description:
      'Premier GCC Tax Research Platform providing comprehensive tax law information',
    foundingDate: '2020',
    knowsAbout: [
      'UAE Corporate Income Tax',
      'GCC Value Added Tax',
      'Excise Tax',
      'Double Taxation Agreements',
      'FTA Decisions and Guidelines',
      'Tax Compliance',
    ],
    areaServed: [
      {
        '@type': 'Place',
        name: 'United Arab Emirates',
      },
      {
        '@type': 'Place',
        name: 'Gulf Cooperation Council',
      },
    ],
    serviceArea: {
      '@type': 'Place',
      name: 'Gulf Cooperation Council',
    },
    sameAs: [
      'https://linkedin.com/company/gcctaxlaws',
      'https://twitter.com/gcctaxlaws',
    ],
  }
}

// Enhanced Helper Functions

const extractFirstSentence = (content) => {
  if (!content) return ''

  // Remove HTML tags and get clean text
  const textOnly = content.replace(/<[^>]*>/g, '').trim()
  if (!textOnly) return ''

  // Find first sentence - look for period followed by space and capital letter, or end of string
  const sentenceEnd = textOnly.search(/\.\s+[A-Z]|\.$/)
  const firstSentence =
    sentenceEnd > 0
      ? textOnly.substring(0, sentenceEnd + 1)
      : textOnly.split('.')[0] + '.'

  // Limit to 160 characters for meta description
  return firstSentence.length > 160
    ? firstSentence.substring(0, 157) + '...'
    : firstSentence
}

const formatDate = (dateString) => {
  if (!dateString) return new Date().toISOString()
  try {
    return new Date(dateString).toISOString()
  } catch {
    return new Date().toISOString()
  }
}

const getWordCount = (content) => {
  if (!content) return 0
  const textOnly = content.replace(/<[^>]*>/g, '').trim()
  return textOnly ? textOnly.split(/\s+/).length : 0
}

const generateKeywords = (item, type) => {
  const baseKeywords = {
    article: [
      'tax law',
      'UAE tax',
      'GCC tax',
      'corporate tax',
      'VAT',
      'tax compliance',
    ],
    decision: [
      'tax decision',
      'FTA ruling',
      'tax authority',
      'UAE tax decision',
      'tax interpretation',
    ],
    guidance: [
      'tax guidance',
      'FTA guide',
      'tax procedures',
      'compliance guide',
      'tax help',
    ],
    treaty: [
      'tax treaty',
      'DTAA',
      'double taxation',
      'international tax',
      'bilateral agreement',
    ],
  }

  let keywords = [...(baseKeywords[type] || [])]

  // Add item-specific keywords
  if (item.title) {
    const titleWords = item.title
      .toLowerCase()
      .replace(/<[^>]*>/g, '')
      .split(/\s+/)
      .filter(
        (word) =>
          word.length > 3 && !['article', 'section', 'chapter'].includes(word)
      )
    keywords.push(...titleWords.slice(0, 3))
  }

  // Add number-based keywords
  if (item.number) {
    keywords.push(`${type} ${item.number}`)
  }

  // Add country-specific keywords
  if (item.slug) {
    const country = getCountryName(item.slug)
    if (country !== 'GCC Region') {
      keywords.push(`${country.toLowerCase()} tax`)
    }
  }

  return [...new Set(keywords)].join(', ')
}

const getCountryName = (slug) => {
  if (!slug) return 'GCC Region'
  if (slug.startsWith('uae-')) return 'United Arab Emirates'
  if (slug.startsWith('ksa-')) return 'Kingdom of Saudi Arabia'
  if (slug.startsWith('qat-')) return 'Qatar'
  if (slug.startsWith('kwt-')) return 'Kuwait'
  if (slug.startsWith('bhr-')) return 'Bahrain'
  if (slug.startsWith('omn-')) return 'Oman'
  return 'GCC Region'
}

const getCountryCode = (slug) => {
  if (!slug) return 'gcc'
  if (slug.startsWith('uae-')) return 'uae'
  if (slug.startsWith('ksa-')) return 'ksa'
  if (slug.startsWith('qat-')) return 'qat'
  if (slug.startsWith('kwt-')) return 'kwt'
  if (slug.startsWith('bhr-')) return 'bhr'
  if (slug.startsWith('omn-')) return 'omn'
  return 'gcc'
}

const getIssuingAuthority = (slug) => {
  if (slug.startsWith('uae-')) return 'Federal Tax Authority'
  if (slug.startsWith('ksa-')) return 'Zakat, Tax and Customs Authority'
  if (slug.startsWith('qat-')) return 'General Tax Authority'
  return 'Tax Authority'
}

const getLawName = (slug) => {
  if (!slug) return 'Tax Law'

  if (slug.includes('cit-fdl-47')) {
    return 'Corporate Income Tax Law - Federal Decree Law No. 47 of 2022'
  }
  if (slug.includes('vat-fdl-8')) {
    return 'Value Added Tax Law - Federal Decree Law No. 8 of 2017'
  }
  if (slug.includes('excise')) {
    return 'Excise Tax Law - Federal Decree Law No. 7 of 2017'
  }
  if (slug.includes('vat')) {
    return 'Value Added Tax Law'
  }
  if (slug.includes('cit')) {
    return 'Corporate Income Tax Law'
  }

  return 'GCC Tax Law'
}

const getLawShortName = (slug) => {
  if (!slug) return 'tax-law'

  if (slug.includes('cit-fdl-47')) return 'cit-fdl-47-of-2022'
  if (slug.includes('vat-fdl-8')) return 'vat-fdl-8-of-2017'
  if (slug.includes('excise')) return 'excise-fdl-7-of-2017'
  if (slug.includes('vat')) return 'vat-law'
  if (slug.includes('cit')) return 'cit-law'

  return 'tax-law'
}
